/**
 * Pull-down-to-open gesture for the reader's book/chapter/verse selector.
 *
 * From the very top of the reader scroll container, dragging the page downward
 * past a commit threshold opens the navigation selector — the natural inverse of
 * the Dialog's pull-down-to-dismiss. It reuses the shared gesture recognizer so
 * the physics (deadzone, axis-lock, fling, rubber-band) match every other
 * gesture in the app.
 *
 * Engagement is gated by `canEngage`: the gesture only claims the vertical axis
 * when the reader is scrolled to the top AND the pull is downward, so normal
 * reading scroll is never hijacked. On a single-axis gesture the recognizer
 * aborts on a perpendicular (horizontal) drag, leaving the chapter swipe and
 * native behaviour intact. Reduced-motion users skip the rubber-band travel and
 * the selector simply opens on commit.
 */

import { createSignal } from "solid-js";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { READER_PULL_NAV } from "@/lib/gestures/presets";

interface ReaderPullNavOptions {
  /** Current vertical scroll offset of the reader (0 == top). */
  getScrollTop: () => number;
  /** Whether motion should be reduced (skip the rubber-band travel). */
  prefersReducedMotion: () => boolean;
  /** Opens the navigation selector once the pull commits. */
  onOpen: () => void;
}

export function useReaderPullNav(options: ReaderPullNavOptions) {
  // Visual pull offset (px) applied to the reader content while dragging.
  const [pullY, setPullY] = createSignal(0);
  const [settling, setSettling] = createSignal(false);

  let committed = false;

  const recognizer = createGestureRecognizer({
    axis: "y",
    thresholdPx: READER_PULL_NAV.thresholdPx,
    fling: { ...READER_PULL_NAV.fling },
    // Only engage a downward pull that starts at the very top of the reader, so
    // mid-chapter scrolling never triggers the selector.
    canEngage: (axis, sample) => axis === "y" && sample.dy > 0 && options.getScrollTop() <= 0,
    onStart: () => {
      committed = false;
      setSettling(false);
      setPullY(0);
    },
    onMove: (sample) => {
      // A finger that scrolls back up past the origin releases the pull.
      const down = Math.max(0, sample.dy);
      setSettling(false);
      setPullY(
        options.prefersReducedMotion()
          ? down
          : applyRubberBand(
              down,
              READER_PULL_NAV.rubberBandLimitPx,
              READER_PULL_NAV.rubberBandFactor,
            ),
      );
    },
    onEnd: (sample) => {
      const reached = sample.dy >= READER_PULL_NAV.commitThresholdPx;
      committed = reached || sample.isFling;
      settle();
    },
    onCancel: settle,
  });

  function settle() {
    setSettling(true);
    setPullY(0);
    if (committed) {
      committed = false;
      vibrate(READER_PULL_NAV.hapticMs);
      options.onOpen();
    }
  }

  const handlers = pointerHandlers(recognizer);

  return {
    pullY,
    settling,
    handlers,
    cancel: () => {
      recognizer.cancel();
    },
  };
}
